// client/src/components/TemplateBuilder.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { LANGUAGES, inputStyle, labelStyle, sectionStyle } from './templateStyles';
import { useIsMobile } from '../hooks/useMediaQuery';
import StandardTemplateForm from './StandardTemplateForm';
import CarouselTemplateForm from './CarouselTemplateForm';

function TemplateBuilder({ token }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [type, setType] = useState('STANDARD'); // STANDARD | CAROUSEL
  const [channelId, setChannelId] = useState('');
  const [language, setLanguage] = useState('es');
  const [category, setCategory] = useState('MARKETING'); // MARKETING | UTILITY
  const [name, setName] = useState('');
  const [channels, setChannels] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    fetch('/api/templates/channels', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        setChannels(data);
        if (data.length > 0) setChannelId(String(data[0].id));
      });
    fetch('/api/templates/custom-fields', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setCustomFields);
  }, [token]);

  const typeBtn = (label, value) => (
    <button key={value} type="button" onClick={() => setType(value)} style={{
      flex: 1, padding: '10px 14px', borderRadius: '8px',
      border: `1px solid ${type === value ? theme.colors.primary : theme.colors.border}`,
      backgroundColor: type === value ? theme.colors.primary : 'transparent',
      color: type === value ? theme.colors.background : theme.colors.text,
      cursor: 'pointer', fontSize: '0.9rem', fontWeight: type === value ? '700' : '400',
    }}>{label}</button>
  );

  const sharedProps = { token, channelId, language, category, name, customFields };

  return (
    <main style={{ padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem', maxWidth: '720px', margin: '0 auto' }}>
      <div style={{
        backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`,
        padding: isMobile ? '1.25rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: 0 }}>Nueva Plantilla</h1>
          <button type="button" onClick={() => navigate('/plantillas')}
            style={{ background: 'none', border: 'none', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>← Volver</button>
        </div>

        {/* Selector de tipo */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {typeBtn('Estándar', 'STANDARD')}
          {typeBtn('Carrusel', 'CAROUSEL')}
        </div>

        {/* Información básica compartida */}
        <div style={sectionStyle}>
          <p style={{ color: theme.colors.primary, fontWeight: '700', fontSize: '0.9rem', margin: '0 0 1rem' }}>Información básica</p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Canal</label>
              <select value={channelId} onChange={(e) => setChannelId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                {channels.length === 0 && <option value="">Sin canales configurados</option>}
                {channels.map((ch) => <option key={ch.id} value={ch.id}>{ch.number}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Idioma</label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Categoría</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="MARKETING">Marketing (promociones, ofertas, novedades)</option>
              <option value="UTILITY">Utilidad (confirmaciones, actualizaciones, alertas)</option>
            </select>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.75rem', margin: '0.3rem 0 0' }}>
              Meta clasifica la plantilla según esta categoría. Las de utilidad suelen aprobarse más rápido y tienen menor costo.
            </p>
          </div>
          <div>
            <label style={labelStyle}>Nombre de la plantilla</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="ej: bienvenida_cliente" style={inputStyle} />
            <p style={{ color: theme.colors.textMuted, fontSize: '0.75rem', margin: '0.3rem 0 0' }}>
              Se convertirá a minúsculas con guiones bajos para Meta.
            </p>
          </div>
        </div>

        {type === 'STANDARD'
          ? <StandardTemplateForm {...sharedProps} />
          : <CarouselTemplateForm {...sharedProps} />}
      </div>
    </main>
  );
}

export default TemplateBuilder;
